import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { TagsEditor } from "@/shared/components/TagsEditor";
import { EventRecord } from "@/shared/domain/events";

import { RobotoTypography } from "../../RobotoTypography";
import { AutocompleteType } from "../../TagAndMetadataAutocomplete";
import {
  useWorkspaceEvent,
  useUpdateWorkspaceEvent,
} from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./EventTags.module.css";

interface EventTagsProps {
  eventId: EventRecord["event_id"];
}

/**
 * An event's tags in the shared {@link TagsEditor}, under a "Tags" heading,
 * each change saved as a metadata changeset.
 */
export function EventTags({ eventId }: EventTagsProps) {
  const eventQuery = useWorkspaceEvent(eventId);
  const { mutateAsync: updateEvent } = useUpdateWorkspaceEvent();

  const save = (metadata_changeset: {
    put_tags?: string[];
    remove_tags?: string[];
  }) => updateEvent({ eventId, request: { metadata_changeset } });

  const heading = (
    <RobotoTypography component={"h3"} className={styles.title}>
      Tags
    </RobotoTypography>
  );

  if (eventQuery.isError) {
    return (
      <section>
        {heading}
        <div className={styles.errorContainer}>
          <ErrorOutlineIcon color="error" />
          <RobotoTypography>Failed to load event tags</RobotoTypography>
        </div>
      </section>
    );
  }

  return (
    <section>
      {heading}
      <TagsEditor
        tags={eventQuery.data?.tags ?? []}
        recordKey={eventId}
        autocompleteType={AutocompleteType.EventTags}
        onAdd={(tag) => save({ put_tags: [tag] })}
        onRemove={(tag) => save({ remove_tags: [tag] })}
        disabled={eventQuery.isPending}
      />
    </section>
  );
}
